import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Tag, Avatar, ProgressBar } from 'antd-mobile';
import { ClockCircleOutline, StarOutline, PlayOutline, EyeOutline, HeartOutline } from 'antd-mobile-icons';
import { useNavigate } from 'react-router-dom';
import TextLightScroll from '../../components/texts/TextLightScroll';

const PersonalizedRecommendationList = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');

  // 模拟个性化推荐数据
  const recommendations = [
    {
      id: 1,
      title: '基于你的Python基础推荐：数据科学入门',
      description: '根据你的编程背景，这门课程将帮助你快速掌握数据分析和机器学习的核心技能。',
      type: 'course',
      category: '数据科学',
      difficulty: '中级',
      duration: '6周',
      rating: 4.8,
      students: 1234,
      instructor: 'Dr. 李明',
      thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=300&h=200&fit=crop',
      tags: ['Python', '数据分析', '机器学习', 'Pandas'],
      progress: 0,
      matchScore: 95,
      reasons: ['基于你的Python技能', '符合你的学习目标', '适合你的时间安排'],
      price: 299,
      originalPrice: 399
    },
    {
      id: 2,
      title: '为你定制：React高级开发实战',
      description: '基于你的前端开发经验，深入学习React生态系统和高级开发模式。',
      type: 'course',
      category: '前端开发',
      difficulty: '高级',
      duration: '8周',
      rating: 4.9,
      students: 856,
      instructor: '张老师',
      thumbnail: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=300&h=200&fit=crop',
      tags: ['React', 'TypeScript', 'Next.js', '状态管理'],
      progress: 25,
      matchScore: 92,
      reasons: ['匹配你的技术栈', '符合职业发展方向', '学习时间灵活'],
      price: 399,
      originalPrice: 599
    },
    {
      id: 3,
      title: '智能推荐：区块链技术与应用',
      description: '基于你对新技术的兴趣，了解区块链的核心原理和实际应用场景。',
      type: 'course',
      category: '区块链',
      difficulty: '中级',
      duration: '4周',
      rating: 4.6,
      students: 567,
      instructor: '王教授',
      thumbnail: 'https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=300&h=200&fit=crop',
      tags: ['区块链', '智能合约', 'Web3', 'DeFi'],
      progress: 0,
      matchScore: 88,
      reasons: ['符合技术趋势', '拓展知识边界', '就业前景良好'],
      price: 199,
      originalPrice: 299
    },
    {
      id: 4,
      title: '个人定制：UI/UX设计思维',
      description: '基于你的设计兴趣，学习用户体验设计的核心方法和实践技巧。',
      type: 'course',
      category: '设计',
      difficulty: '初级',
      duration: '5周',
      rating: 4.7,
      students: 923,
      instructor: '设计师小美',
      thumbnail: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=300&h=200&fit=crop',
      tags: ['UI设计', 'UX设计', 'Figma', '用户研究'],
      progress: 60,
      matchScore: 85,
      reasons: ['培养设计思维', '提升综合能力', '跨领域发展'],
      price: 249,
      originalPrice: 349
    },
    {
      id: 5,
      title: '推荐学习：云计算与DevOps实践',
      description: '根据行业发展趋势，掌握现代软件开发和部署的最佳实践。',
      type: 'course',
      category: '云计算',
      difficulty: '中级',
      duration: '7周',
      rating: 4.8,
      students: 678,
      instructor: '运维专家',
      thumbnail: 'https://via.placeholder.com/300x200?text=云计算DevOps',
      tags: ['AWS', 'Docker', 'Kubernetes', 'CI/CD'],
      progress: 0,
      matchScore: 82,
      reasons: ['行业热门技能', '提升竞争力', '薪资增长潜力'],
      price: 349,
      originalPrice: 499
    },
    {
      id: 6,
      title: '智能匹配：移动应用开发',
      description: '基于你的编程基础，学习跨平台移动应用开发技术。',
      type: 'course',
      category: '移动开发',
      difficulty: '中级',
      duration: '6周',
      rating: 4.5,
      students: 445,
      instructor: '移动开发团队',
      thumbnail: 'https://via.placeholder.com/300x200?text=移动应用开发',
      tags: ['React Native', 'Flutter', 'iOS', 'Android'],
      progress: 0,
      matchScore: 78,
      reasons: ['技能互补', '市场需求大', '学习路径清晰'],
      price: 279,
      originalPrice: 379
    }
  ];

  const filters = [
    { key: 'all', label: '全部推荐', count: recommendations.length },
    { key: 'high_match', label: '高匹配度', count: recommendations.filter(r => r.matchScore >= 90).length },
    { key: 'in_progress', label: '学习中', count: recommendations.filter(r => r.progress > 0).length },
    { key: 'new', label: '新推荐', count: recommendations.filter(r => r.progress === 0).length }
  ];

  const filteredRecommendations = recommendations.filter(recommendation => {
    switch (activeFilter) {
      case 'high_match':
        return recommendation.matchScore >= 90;
      case 'in_progress':
        return recommendation.progress > 0;
      case 'new':
        return recommendation.progress === 0;
      default:
        return true;
    }
  });

  const getDifficultyColor = (difficulty: string) => {
    const colors: { [key: string]: string } = {
      '初级': '#4CAF50',
      '中级': '#FF9800',
      '高级': '#F44336'
    };
    return colors[difficulty] || '#666';
  };

  const getMatchScoreColor = (score: number) => {
    if (score >= 90) return '#4CAF50';
    if (score >= 80) return '#FF9800';
    return '#666';
  };

  return (
    <StyledWrapper>
      <div className="recommendation-list-container">
        <div className="header">
          <div className="title-section">
            <TextLightScroll text="个性化推荐" />
            <p>基于你的学习历史、技能水平和兴趣偏好为你精心推荐</p>
          </div>
          
          <div className="stats-section">
            <div className="stat-item">
              <span className="stat-number">{recommendations.length}</span>
              <span className="stat-label">推荐课程</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{Math.round(recommendations.reduce((sum, r) => sum + r.matchScore, 0) / recommendations.length)}%</span>
              <span className="stat-label">平均匹配度</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">{recommendations.filter(r => r.progress > 0).length}</span>
              <span className="stat-label">学习中</span>
            </div>
          </div>
        </div>

        {/* 筛选器 */}
        <div className="filters-container">
          {filters.map(filter => (
            <button
              key={filter.key}
              className={`filter-btn ${activeFilter === filter.key ? 'active' : ''}`}
              onClick={() => setActiveFilter(filter.key)}
            >
              {filter.label}
              <span className="count">({filter.count})</span>
            </button>
          ))}
        </div>

        {/* 推荐说明 */}
        <div className="recommendation-info">
          <div className="info-content">
            <div className="info-icon">🎯</div>
            <div className="info-text">
              <h3>智能推荐算法</h3>
              <p>我们基于你的学习行为、技能评估、职业目标等多维度数据，为你推荐最适合的学习内容</p>
            </div>
          </div>
        </div>

        {/* 推荐列表 */}
        <div className="recommendations-grid">
          {filteredRecommendations.map(recommendation => (
            <div 
              key={recommendation.id} 
              className="recommendation-card"
              onClick={() => navigate(`/learn/recommendation/${recommendation.id}`)}
            >
              {/* 匹配度标签 */}
              <div className="match-badge" style={{ backgroundColor: getMatchScoreColor(recommendation.matchScore) }}>
                {recommendation.matchScore}% 匹配
              </div>
              
              {/* 课程缩略图 */}
              <div className="course-thumbnail">
                <img src={recommendation.thumbnail} alt={recommendation.title} />
                {recommendation.progress > 0 && (
                  <div className="progress-overlay">
                    <ProgressBar percent={recommendation.progress} />
                    <span className="progress-text">已学习 {recommendation.progress}%</span>
                  </div>
                )}
              </div>
              
              {/* 课程信息 */}
              <div className="course-content">
                <div className="course-header">
                  <h3 className="course-title">{recommendation.title}</h3>
                  <div className="course-meta">
                    <span className="category">{recommendation.category}</span>
                    <span 
                      className="difficulty" 
                      style={{ color: getDifficultyColor(recommendation.difficulty) }}
                    >
                      {recommendation.difficulty}
                    </span>
                  </div>
                </div>
                
                <p className="course-description">{recommendation.description}</p>
                
                {/* 推荐理由 */}
                <div className="recommendation-reasons">
                  <h4>推荐理由：</h4>
                  <div className="reasons-list">
                    {recommendation.reasons.slice(0, 2).map((reason, index) => (
                      <span key={index} className="reason-tag">✓ {reason}</span>
                    ))}
                  </div>
                </div>
                
                {/* 课程标签 */}
                <div className="course-tags">
                  {recommendation.tags.slice(0, 3).map((tag, index) => (
                    <span key={index} className="course-tag">{tag}</span>
                  ))}
                  {recommendation.tags.length > 3 && (
                    <span className="more-tags">+{recommendation.tags.length - 3}</span>
                  )}
                </div>
                
                {/* 课程统计 */}
                <div className="course-stats">
                  <div className="stat">
                    <StarOutline />
                    <span>{recommendation.rating}</span>
                  </div>
                  <div className="stat">
                    <EyeOutline />
                    <span>{recommendation.students}人学习</span>
                  </div>
                  <div className="stat">
                    <ClockCircleOutline />
                    <span>{recommendation.duration}</span>
                  </div>
                </div>
                
                {/* 讲师信息 */}
                <div className="instructor-info">
                  <Avatar src="https://via.placeholder.com/24" style={{ width: '24px', height: '24px' }} />
                  <span className="instructor-name">{recommendation.instructor}</span>
                </div>
                
                {/* 价格和操作 */}
                <div className="course-footer">
                  <div className="price-section">
                    <span className="current-price">¥{recommendation.price}</span>
                    <span className="original-price">¥{recommendation.originalPrice}</span>
                  </div>
                  
                  <div className="action-buttons">
                    <Button 
                      size="small" 
                      fill="none" 
                      className="favorite-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        // 收藏逻辑
                      }}
                    >
                      <HeartOutline />
                    </Button>
                    <button 
                      className="start-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/learn/recommendation/${recommendation.id}`);
                      }}
                    >
                      {recommendation.progress > 0 ? (
                        <><PlayOutline /> 继续学习</>
                      ) : (
                        '立即学习'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {filteredRecommendations.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📚</div>
            <h3>暂无推荐内容</h3>
            <p>请尝试调整筛选条件或完善你的学习档案</p>
            <Button color="primary" onClick={() => setActiveFilter('all')}>
              查看全部推荐
            </Button>
          </div>
        )}
      </div>
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  .recommendation-list-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 40px 24px;
    background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
    min-height: 100vh;
  }

  .header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 40px;
    color: #1f2937;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 20px;
    }
  }

  .title-section {
    p {
      font-size: 1.2rem;
      color: #6b7280;
      margin: 20px 0 0 0;
      line-height: 1.6;
    }
  }

  .stats-section {
    display: flex;
    gap: 30px;
    
    @media (max-width: 768px) {
      gap: 20px;
    }
  }

  .stat-item {
    text-align: center;
    background: rgba(255, 255, 255, 0.8);
    padding: 20px;
    border-radius: 16px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    
    .stat-number {
      display: block;
      font-size: 2rem;
      font-weight: bold;
      color: #6366f1;
      line-height: 1;
    }
    
    .stat-label {
      font-size: 0.9rem;
      color: #6b7280;
      margin-top: 5px;
    }
  }

  .filters-container {
    display: flex;
    gap: 12px;
    margin-bottom: 30px;
    flex-wrap: wrap;
  }

  .filter-btn {
    background: rgba(255, 255, 255, 0.8);
    border: 1px solid rgba(99, 102, 241, 0.2);
    color: #6b7280;
    padding: 12px 20px;
    border-radius: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.9rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 6px;
    backdrop-filter: blur(10px);
    
    &:hover {
      background: rgba(255, 255, 255, 0.9);
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
    }
    
    &.active {
      background: #6366f1;
      border-color: #6366f1;
      color: white;
      box-shadow: 0 8px 25px rgba(99, 102, 241, 0.3);
    }
    
    .count {
      font-size: 0.8rem;
      opacity: 0.8;
    }
  }

  .recommendation-info {
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 20px;
    margin-bottom: 40px;
    padding: 24px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }

  .info-content {
    display: flex;
    align-items: center;
    gap: 16px;
    color: #1f2937;
    
    .info-icon {
      font-size: 2.5rem;
    }
    
    .info-text {
      h3 {
        margin: 0 0 8px 0;
        font-size: 1.3rem;
        font-weight: 700;
        color: #1f2937;
      }
      
      p {
        margin: 0;
        color: #6b7280;
        line-height: 1.5;
        font-size: 1rem;
      }
    }
  }

  .recommendations-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: 30px;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: 24px;
    }
  }

  .recommendation-card {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 24px;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
    
    &:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
      border-color: rgba(99, 102, 241, 0.3);
    }
  }

  .match-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    color: white;
    padding: 8px 16px;
    border-radius: 20px;
    font-size: 0.8rem;
    font-weight: 600;
    z-index: 2;
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  }

  .course-thumbnail {
    position: relative;
    height: 220px;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
    
    &:hover img {
      transform: scale(1.1);
    }
  }

  .progress-overlay {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
    padding: 15px;
    color: white;
    
    .progress-text {
      font-size: 0.8rem;
      margin-top: 5px;
      display: block;
    }
  }

  .course-content {
    padding: 24px;
    color: #1f2937;
  }

  .course-header {
    margin-bottom: 16px;
    
    .course-title {
      font-size: 1.3rem;
      font-weight: 700;
      margin: 0 0 12px 0;
      line-height: 1.4;
      color: #1f2937;
    }
    
    .course-meta {
      display: flex;
      gap: 12px;
      
      .category {
        background: rgba(99, 102, 241, 0.1);
        color: #6366f1;
        padding: 6px 12px;
        border-radius: 12px;
        font-size: 0.8rem;
        font-weight: 500;
      }
      
      .difficulty {
        font-size: 0.8rem;
        font-weight: 600;
      }
    }
  }

  .course-description {
    font-size: 0.95rem;
    line-height: 1.6;
    color: #6b7280;
    margin-bottom: 18px;
  }

  .recommendation-reasons {
    margin-bottom: 18px;
    background: rgba(16, 185, 129, 0.1);
    padding: 16px;
    border-radius: 12px;
    border: 1px solid rgba(16, 185, 129, 0.2);
    
    h4 {
      font-size: 0.9rem;
      margin: 0 0 10px 0;
      color: #10b981;
      font-weight: 600;
    }
    
    .reasons-list {
      display: flex;
      flex-direction: column;
      gap: 6px;
      
      .reason-tag {
        font-size: 0.85rem;
        color: #059669;
        font-weight: 500;
      }
    }
  }

  .course-tags {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 18px;
    align-items: center;
    
    .course-tag {
      font-size: 0.8rem;
      background: rgba(99, 102, 241, 0.1);
      color: #6366f1;
      padding: 6px 12px;
      border-radius: 12px;
      font-weight: 500;
    }
    
    .more-tags {
      font-size: 0.8rem;
      color: #f59e0b;
      background: rgba(245, 158, 11, 0.1);
      padding: 6px 12px;
      border-radius: 12px;
      font-weight: 500;
    }
  }

  .course-stats {
    display: flex;
    gap: 16px;
    margin-bottom: 18px;
    
    .stat {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 0.85rem;
      color: #6b7280;
      font-weight: 500;
    }
  }

  .instructor-info {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 18px;
    
    .instructor-name {
      font-size: 0.9rem;
      color: #6b7280;
      font-weight: 500;
    }
  }

  .course-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 18px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
  }

  .price-section {
    display: flex;
    align-items: center;
    gap: 10px;
    
    .current-price {
      font-size: 1.3rem;
      font-weight: bold;
      color: #f59e0b;
    }
    
    .original-price {
      font-size: 0.9rem;
      text-decoration: line-through;
      color: #9ca3af;
    }
  }

  .action-buttons {
    display: flex;
    gap: 10px;
    align-items: center;
    
    .favorite-btn {
      color: #6b7280;
      border-color: rgba(107, 114, 128, 0.3);
      background: rgba(255, 255, 255, 0.8);
      
      &:hover {
        color: #ef4444;
        border-color: #ef4444;
        background: rgba(239, 68, 68, 0.1);
      }
    }
  }

  .empty-state {
    text-align: center;
    padding: 80px 20px;
    color: #1f2937;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 24px;
    backdrop-filter: blur(20px);
    
    .empty-icon {
      font-size: 4rem;
      margin-bottom: 24px;
    }
    
    h3 {
      font-size: 1.6rem;
      font-weight: 700;
      margin-bottom: 12px;
      color: #1f2937;
    }
    
    p {
      color: #6b7280;
      margin-bottom: 30px;
      font-size: 1.1rem;
    }
  }
`;

export default PersonalizedRecommendationList;